import { OrbindApiError } from '@orbind/api-client'
import { diasEntre } from '@orbind/core'
import { useToast } from '@orbind/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api.ts'
import type { Bootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { useWiMutation, type WorkItemDetail } from './api.ts'
import { Dropdown, NumberStepper, initialsOf, TONE_COLOR } from './bits.tsx'
import { CamposComplementaresProps } from './CamposComplementaresProps.tsx'
import { CamposComplementaresForm } from './CamposComplementaresForm.tsx'
import { ReabrirModal } from './ReabrirModal.tsx'
import { DescerEmpresaModal } from './DescerEmpresaModal.tsx'
import { CampoInfo, useSystemFieldHelp } from './CampoInfo.tsx'
import { DatesPanel, MembersPanel } from './panels.tsx'
import { Fragment } from 'react'

/**
 * Painel "Propriedades" (fatia 06): redimensionavel por arraste no divisor,
 * recolhivel pelo botao central, com selects em popover — simples, com busca
 * (responsavel) e em arvore (portfolio).
 */

type PropDef = {
  key: string
  label: string
  searchable?: boolean
  tree?: boolean
  readonly?: boolean
  /**
   * Nasce DESLIGADO: só aparece na barra se for marcado na configuração
   * (pedido 2026-08-31). Para esses, a marca guardada significa "mostrar",
   * ao contrário dos demais, em que ela significa "esconder".
   */
  ocultoPorPadrao?: boolean
}

const PROPS: readonly PropDef[] = [
  // Datas: PRIMEIRO campo da barra — só aparece quando o item tem data
  // (antes ficavam como chip no corpo do item, 2026-08-29)
  { key: 'datas', label: 'Datas' },
  // Quantidade de dias logo abaixo das datas, com + e - (2026-08-31): da para
  // planejar a duracao sem abrir o painel de datas
  { key: 'dias', label: 'Quantidade de dias' },
  // Membros logo abaixo das datas (antes eram chips no corpo, 2026-08-29)
  { key: 'membros', label: 'Membros' },
  // Etapa, Status, Prioridade no topo (movidos das pílulas do cabeçalho, 2026-08-29)
  { key: 'etapa', label: 'Etapa' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Prioridade' },
  // Área e Responsável (ordem trocada, 2026-08-29)
  { key: 'area', label: 'Área' },
  { key: 'assignee', label: 'Responsável', searchable: true },
  { key: 'company', label: 'Empresa' },
  // em quais quadros o item pode aparecer; vazio = Todos (SPEC-20261007-1538-quadros-do-item)
  { key: 'quadros', label: 'Quadros' },
  { key: 'portfolio', label: 'Item do portfólio', tree: true, ocultoPorPadrao: true },
  { key: 'createdAt', label: 'Criado em', readonly: true },
]

export function PropsPanel({
  detail,
  bootstrap,
  modoInclusao = false,
  modoTemplate = false,
}: {
  detail: WorkItemDetail
  bootstrap: Bootstrap
  /** Item ainda em inclusão: trocar a etapa não gera movimentação (2026-08-29). */
  modoInclusao?: boolean
  /**
   * MODO TEMPLATE (SPEC-20260830-1530): a etapa NAO segue a conducao do fluxo.
   * Olha so o TIPO e oferece a PRIMEIRA etapa de cada fluxo dele — um modelo
   * nasce no comeco, nao no meio do caminho (regra do usuario).
   */
  modoTemplate?: boolean
}) {
  const t = useT()
  const toast = useToast()
  /** Quantos filhos esperam resposta sobre a empresa; null = ninguem pergunta. */
  const [descendoEmpresa, setDescendoEmpresa] = useState<number | null>(null)
  const [width, setWidth] = useState(290)
  const [collapsed, setCollapsed] = useState(false)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [expandedNodes, setExpandedNodes] = useState<ReadonlySet<string>>(new Set())
  const resizing = useRef(false)

  const item = detail.item
  /** Seções desligadas no menu "..." do item (vem como JSON do banco). */
  const secoesOcultas: string[] = Array.isArray(item.hiddenSections) ? (item.hiddenSections as string[]) : []
  const [configAberta, setConfigAberta] = useState(false)
  /**
   * Liga/desliga um campo da barra. Guarda no MESMO lugar das seções do item
   * (`hiddenSections`, com a chave `prop:<campo>`): é a mesma natureza de
   * decisão — o que este item mostra —, então vale para quem abrir depois.
   */
  const alternarUmCampo = useWiMutation(item.id, async (input: { chave: string; visivel: boolean }) =>
    api['work-items'][':id'].sections[':chave'].toggle.$post({
      param: { id: item.id, chave: input.chave },
      json: { visivel: input.visivel },
    }),
  )
  /** Põe (ou tira) uma marca na lista de ajustes de exibição do item. */
  const definirMarca = (chave: string, presente: boolean) =>
    alternarUmCampo.mutate({ chave, visivel: !presente })
  /** A marca de um campo: `prop:` esconde; `mostrar:` revela o que nasce oculto. */
  const marcaDe = (prop: PropDef) => (prop.ocultoPorPadrao ? `mostrar:${prop.key}` : `prop:${prop.key}`)
  const campoVisivel = (prop: PropDef) =>
    prop.ocultoPorPadrao ? secoesOcultas.includes(marcaDe(prop)) : !secoesOcultas.includes(marcaDe(prop))
  const alternarCampo = (prop: PropDef) => {
    const querVisivel = !campoVisivel(prop)
    definirMarca(marcaDe(prop), prop.ocultoPorPadrao ? querVisivel : !querVisivel)
  }

  /*
    QUADROS do item (SPEC-20261007-1538-quadros-do-item): a lista de quadros do workspace para
    escolher, e os escolhidos. Nenhum escolhido = aparece em Todos.
  */
  const quadrosDoItem = ((detail as { quadros?: { id: string; name: string }[] }).quadros ?? [])
  const idsDosQuadros = new Set(quadrosDoItem.map((q) => q.id))
  const todosOsQuadros = useQuery({
    queryKey: ['kanbans'],
    queryFn: async () => (await api.kanbans.$get()).json(),
    enabled: openMenu === 'quadros',
  })
  const salvarQuadros = useWiMutation(item.id, async (boardIds: string[]) =>
    api['work-items'][':id'].quadros.$put({ param: { id: item.id }, json: { boardIds } }),
  )
  const alternarQuadro = (boardId: string) => {
    const proximos = idsDosQuadros.has(boardId)
      ? [...idsDosQuadros].filter((x) => x !== boardId)
      : [...idsDosQuadros, boardId]
    salvarQuadros.mutate(proximos)
  }

  const patch = useWiMutation(item.id, async (json: Record<string, string | null>) =>
    api['work-items'][':id'].$patch({
      param: { id: item.id },
      // em modo de inclusão a troca de etapa não vira movimentação
      query: modoInclusao ? { rascunho: '1' } : {},
      json: json as never,
    }),
  )
  // status usa endpoint proprio (checa criterios obrigatorios pendentes)
  const changeStatus = useWiMutation(
    item.id,
    async (input: { statusId: string; comment?: string; flowStepId?: string | null }) =>
      api['work-items'][':id'].status.$post({ param: { id: item.id }, json: input as never }),
  )
  /** Reabertura em curso: status de destino escolhido, aguardando o modal. */
  const [reabrindo, setReabrindo] = useState<{ id: string; label: string } | null>(null)
  // rotulo traduzido de um catalogo (status/prioridade); cai no code se sem traducao
  const catalogLabel = (row: { code: string; nameTranslationKey: string } | undefined) => {
    if (!row) return '—'
    const label = t(row.nameTranslationKey)
    return label === row.nameTranslationKey ? row.code : label
  }

  const portfolio = useQuery({
    queryKey: ['portfolio', 'tree'],
    queryFn: async () => (await api.portfolio.tree.$get()).json(),
    enabled: openMenu === 'portfolio',
  })

  // nos de fluxo do workspace (fluxo · etapa) — para exibir/trocar a etapa
  const flowNodes = useQuery({
    queryKey: ['flow-nodes'],
    queryFn: async () => (await api.flows.nodes.$get()).json(),
    // também carrega quando o item ainda não tem etapa: o tipo pode ter fluxo
    // e nesse caso a etapa inicial é preenchida sozinha
    enabled: true,
    retry: false,
  })

  // Regras do seletor (2026-08-25): campo FLUXO escolhe o fluxo (editavel so
  // quando o item nao tem etapa — com etapa, o fluxo e o da etapa); o campo
  // ETAPA lista as etapas do fluxo escolhido (ordem do desenho) ou, com
  // etapa, so as PROXIMAS do caminho.
  const [fluxoEscolhido, setFluxoEscolhido] = useState<string | null>(null)
  /** Tipo flexível (sem fluxo): "Trocar de fluxo…" mostra todos os fluxos. */
  const [trocandoFluxo, setTrocandoFluxo] = useState(false)
  const nosDeFluxo = flowNodes.data?.nodes ?? []
  const ligacoesDeFluxo = flowNodes.data?.transitions ?? []
  const proximasDoItem = (() => {
    const atual = item.flowStepId ? nosDeFluxo.find((n) => n.flowStepId === item.flowStepId) : undefined
    if (!atual) return []
    const irmas = new Set(nosDeFluxo.filter((n) => n.processStepId === atual.processStepId).map((n) => n.flowStepId))
    const ids = new Set(ligacoesDeFluxo.filter((l) => irmas.has(l.from)).map((l) => l.to))
    return nosDeFluxo.filter((n) => ids.has(n.flowStepId))
  })()
  const etapasEmOrdem = (flowId: string) => {
    const doFluxo = nosDeFluxo.filter((n) => n.flowId === flowId)
    const trans = ligacoesDeFluxo.filter((t) => t.flowId === flowId)
    const proximos = new Map<string, string[]>()
    for (const t of trans) proximos.set(t.from, [...(proximos.get(t.from) ?? []), t.to])
    const comEntrada = new Set(trans.map((t) => t.to))
    const porId = new Map(doFluxo.map((n) => [n.flowStepId, n]))
    const vistos = new Set<string>()
    const ordem: typeof doFluxo = []
    const caminhar = (id: string) => {
      if (vistos.has(id)) return
      vistos.add(id)
      const n = porId.get(id)
      if (n) ordem.push(n)
      for (const p of proximos.get(id) ?? []) caminhar(p)
    }
    for (const n of doFluxo) if (!comEntrada.has(n.flowStepId)) caminhar(n.flowStepId)
    for (const n of doFluxo) if (!vistos.has(n.flowStepId)) caminhar(n.flowStepId)
    return ordem
  }
  /**
   * REGRAS DE ETAPA POR FLUXO (2026-08-29):
   * - o item só pode entrar nos fluxos do SEU TIPO;
   * - "sem etapa" vale para qualquer tipo (SPEC-20261007-2140-item-sem-fluxo): o item
   *   pode nascer e ficar fora de fluxo. Até 2026-10-07 tipo com fluxo exigia
   *   etapa, e a barra gravava sozinha a inicial ao abrir o item.
   */
  const tipoDoItem = bootstrap.workItemTypes.find((tp) => tp.id === item.workItemTypeId)
  const fluxosDoTipo = new Set(((tipoDoItem as { flowIds?: string[] } | undefined)?.flowIds) ?? [])
  const tipoTemFluxo = fluxosDoTipo.size > 0
  const fluxosUnicos = [...new Map(nosDeFluxo.map((n) => [n.flowId, n.flowName])).entries()].filter(
    ([fid]) => !tipoTemFluxo || fluxosDoTipo.has(fid),
  )

  /**
   * Em MODO DE INCLUSÃO só se escolhe a PRIMEIRA etapa de cada fluxo
   * disponível — o item ainda está nascendo, então a lista não avança para as
   * próximas etapas até ele ser salvo (2026-08-29).
   */
  const etapasIniciaisDisponiveis = fluxosUnicos
    .flatMap(([fid]) => {
      const ordem = etapasEmOrdem(fid)
      // INÍCIO DE FLUXO marcado no desenho (2026-09-02): quando o fluxo tem
      // etapas marcadas, TODAS elas podem ser a primeira; sem nenhuma, vale
      // a primeira do desenho, como antes.
      const marcadas = ordem.filter((n) => (n as { isInitial?: boolean }).isInitial)
      if (marcadas.length > 0) return marcadas
      return ordem[0] ? [ordem[0]] : []
    })

  // Item sem etapa NÃO ganha mais a inicial sozinho ao abrir (SPEC-20261007-2140-item-sem-fluxo):
  // gravar isso por conta própria punha num fluxo o item que nasceu fora dele.

  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      if (!resizing.current) return
      // Pode avancar ate ~60% da janela em direcao ao centro.
      const max = Math.max(430, window.innerWidth * 0.6)
      setWidth(Math.max(220, Math.min(max, window.innerWidth - event.clientX - 28)))
    }
    const onUp = () => {
      resizing.current = false
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  const members = bootstrap.members.filter((c) => c.isActive)
  // membros DO ITEM (colaboração) — o campo "Membros" da barra mostra estes
  const membrosDoItem = bootstrap.members.filter((m) => detail.collab.memberIds.includes(m.id))
  const areaName = (id: string | null) => {
    const area = bootstrap.areas.find((a) => a.id === id)
    if (!area) return null
    const label = t(area.nameTranslationKey)
    return label === area.nameTranslationKey ? area.code : label
  }

  // aceita so classe FontAwesome como icone inline (URL de imagem cai no fallback)
  const faClasse = (icon: string | null | undefined) =>
    icon && !icon.startsWith('/') && !icon.startsWith('http') && icon.includes('fa') ? icon : null

  const valueOf = (
    prop: PropDef,
  ): { text: string | null; avatar?: string; dot?: string; icon?: { cls: string; color?: string } } => {
    switch (prop.key) {
      case 'assignee': {
        const person = members.find((c) => c.id === item.assigneeMemberId)
        return { text: person?.name ?? null, avatar: person ? initialsOf(person.name) : undefined }
      }
      case 'status': {
        const s = bootstrap.workItemStatuses.find((x) => x.id === item.workItemStatusId)
        if (!s) return { text: null }
        const cor = TONE_COLOR[s.tone] ?? s.color ?? undefined
        const cls = faClasse(s.icon)
        return cls ? { text: catalogLabel(s), icon: { cls, color: cor } } : { text: catalogLabel(s), dot: cor }
      }
      case 'priority': {
        const p = bootstrap.priorities.find((x) => x.id === item.workItemPriorityId)
        if (!p) return { text: null }
        const cor = TONE_COLOR[p.tone] ?? p.color ?? undefined
        // bandeirinha por padrao; ou o icone escolhido no catalogo, colorido pelo tom
        return { text: catalogLabel(p), icon: { cls: faClasse(p.icon) ?? 'fas fa-flag', color: cor } }
      }
      case 'area':
        return { text: areaName(item.companyAreaId) }
      case 'company':
        return { text: bootstrap.companies.find((c) => c.id === item.companyId)?.name ?? null }
      case 'quadros':
        // nenhum escolhido: aparece em todos os quadros, e o campo diz isso
        return {
          text: quadrosDoItem.length === 0 ? 'Todos' : quadrosDoItem.map((q) => t(q.name)).join(', '),
          icon: { cls: 'fas fa-columns' },
        }
      case 'portfolio':
        return { text: null } // resolvido abaixo com a query da arvore quando aberta
      case 'etapa': {
        if (!item.flowStepId) return { text: null }
        const no = (flowNodes.data?.nodes ?? []).find((n) => n.flowStepId === item.flowStepId)
        if (!no) return { text: '…', icon: { cls: 'fas fa-map-signs' } }
        // "titulo da etapa / nome do fluxo"; o icone (placa de etapa do processo)
        // fica colorido pela etapa
        return { text: `${no.stepName} / ${no.flowName}`, icon: { cls: 'fas fa-map-signs', color: no.stepColor ?? 'var(--ob-primary)' } }
      }
      case 'datas': {
        // "início — entrega" (ou só uma delas), como o chip que existia no corpo
        const fmt = (v: string | Date) =>
          new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(new Date(v)).replace('.', '')
        if (item.startAt && item.dueAt) {
          return { text: `${fmt(item.startAt)} — ${fmt(item.dueAt)}`, icon: { cls: 'far fa-calendar', color: 'var(--ob-primary)' } }
        }
        if (item.dueAt) {
          const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(item.dueAt))
          return { text: `Entrega ${fmt(item.dueAt)}, ${hora}`, icon: { cls: 'far fa-calendar', color: 'var(--ob-primary)' } }
        }
        if (item.startAt) {
          return { text: `Início ${fmt(item.startAt)}`, icon: { cls: 'far fa-calendar', color: 'var(--ob-primary)' } }
        }
        // sem data, mas com duracao: o prazo sai da cadeia (2026-08-31)
        if (item.durationDays != null) {
          return {
            text: `${item.durationDays} dia${item.durationDays === 1 ? '' : 's'} · pela cadeia`,
            icon: { cls: 'far fa-clock', color: 'var(--ob-text-muted)' },
          }
        }
        return { text: null }
      }
      case 'dias':
        return { text: item.durationDays != null ? String(item.durationDays) : null }
      case 'createdAt':
        return {
          text: new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(item.createdAt)),
        }
      default:
        return { text: null }
    }
  }

  /** Instrucoes dos campos de sistema (padrao + edicoes do catalogo). */
  const ajudaSistema = useSystemFieldHelp()
  const ajudaDe = (key: string) => ajudaSistema.data?.find((f) => f.key === `prop:${key}`)?.help ?? null
  const mapaFieldHelp = (item as { fieldHelp?: Record<string, string> }).fieldHelp

  /** Grava so a quantidade de dias — o campo da barra nao abre painel. */
  const salvarDias = useWiMutation(item.id, async (dias: number | null) =>
    api['work-items'][':id'].$patch({ param: { id: item.id }, query: {}, json: { durationDays: dias } as never }),
  )

  const [portfolioName, setPortfolioName] = useState<string | null>(null)
  useEffect(() => {
    if (!item.portfolioNodeId) {
      setPortfolioName(null)
      return
    }
    // nome do no atual: busca leve no detalhe do portfolio
    api.portfolio[':id']
      .$get({ param: { id: item.portfolioNodeId } })
      .then((r) => r.json())
      .then((data) => setPortfolioName(data.node.name))
      .catch(() => setPortfolioName(null))
  }, [item.portfolioNodeId])

  const selectValue = (prop: PropDef, value: string | null) => {
    // status: endpoint proprio (barra criterios obrigatorios pendentes)
    if (prop.key === 'status') {
      if (value) {
        const atual = bootstrap.workItemStatuses.find((s) => s.id === item.workItemStatusId)
        const destino = bootstrap.workItemStatuses.find((s) => s.id === value)
        // sair de um status terminal e REABRIR: pede motivo e etapa antes
        if (atual?.isTerminal && destino && !destino.isTerminal) {
          setReabrindo({ id: value, label: catalogLabel(destino) })
          setOpenMenu(null)
          return
        }
        changeStatus.mutate({ statusId: value }, {
          onError: (error) => {
            if (error instanceof OrbindApiError && error.code === 'completion_blocked') {
              toast.push({ tone: 'warning', message: 'Conclua os critérios obrigatórios antes de finalizar este Item de Trabalho.' })
            } else {
              toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao mudar o status.' })
            }
          },
        })
      }
      setOpenMenu(null)
      return
    }
    const field =
      prop.key === 'assignee'
        ? 'assigneeMemberId'
        : prop.key === 'priority'
          ? 'workItemPriorityId'
          : prop.key === 'area'
            ? 'companyAreaId'
            : prop.key === 'company'
              ? 'companyId'
              : prop.key === 'etapa'
                ? 'flowStepId'
                : 'portfolioNodeId'
    /*
      EMPRESA: depois de gravar no item, pergunta se desce para os filhos
      (SPEC-20261006-1027-empresa-desce-para-os-filhos). Um item filho costuma ser trabalho da mesma
      empresa, mas nem sempre — e descer sozinho mudaria coisa que ninguém
      mandou mudar.

      A pergunta só aparece quando HÁ filhos: um diálogo com um caminho
      possível é um clique a mais sem escolha nenhuma.
    */
    if (field === 'companyId') {
      patch.mutate(
        { [field]: value },
        {
          onSuccess: async () => {
            try {
              const r = await api['work-items'][':id'].descendentes.$get({ param: { id: item.id } })
              const { total } = await r.json()
              // `total` é a DESCENDÊNCIA: o caminho materializado de um item
              // guarda os ancestrais dele, não ele mesmo
              if (total > 0) setDescendoEmpresa(total)
            } catch {
              // não saber quantos filhos há não pode atrapalhar a gravação que
              // já deu certo; a pessoa muda os filhos à mão se quiser
            }
          },
        },
      )
    } else {
      patch.mutate({ [field]: value })
    }
    setOpenMenu(null)
    setQuery('')
    setTrocandoFluxo(false)
  }

  const toggleNode = (key: string) =>
    setExpandedNodes((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  if (collapsed) {
    return (
      <div className="wi-resizer">
        <button
          type="button"
          className="wi-resizer__toggle"
          aria-label="Mostrar propriedades"
          title="Mostrar propriedades"
          onClick={() => setCollapsed(false)}
        >
          <i className="fas fa-chevron-left" aria-hidden style={{ fontSize: 9 }} />
        </button>
      </div>
    )
  }

  const treeNodes = (portfolio.data?.nodes ?? []) as { id: string; parentId: string | null; name: string; level: string; depth: number }[]
  const visibleTree = treeNodes.filter((node) => {
    if (!node.parentId) return true
    // mostra se todos os ancestrais estao expandidos
    let current = treeNodes.find((n) => n.id === node.parentId)
    while (current) {
      if (!expandedNodes.has(current.id)) return false
      current = treeNodes.find((n) => n.id === current!.parentId)
    }
    return true
  })

  return (
    <>
      <div
        className="wi-resizer"
        title="Arraste para redimensionar · use a seta para recolher"
        onMouseDown={() => {
          resizing.current = true
          document.body.style.userSelect = 'none'
        }}
      >
        <button
          type="button"
          className="wi-resizer__toggle"
          aria-label="Recolher propriedades"
          title="Recolher propriedades"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setCollapsed(true)}
        >
          <i className="fas fa-chevron-right" aria-hidden style={{ fontSize: 9 }} />
        </button>
      </div>
      {/* Empresa mudou e o item tem filhos: desce ou nao (SPEC-20261006-1027-empresa-desce-para-os-filhos) */}
      {descendoEmpresa !== null ? (
        <DescerEmpresaModal
          itemId={item.id}
          filhos={descendoEmpresa}
          empresa={bootstrap.companies.find((c) => c.id === item.companyId)?.name ?? 'Sem empresa'}
          onFechar={() => setDescendoEmpresa(null)}
        />
      ) : null}

      <aside className="wi-props" style={{ width }}>
        <ReabrirModal
          aberto={reabrindo !== null}
          codigo={item.code}
          statusDestino={reabrindo?.label ?? ''}
          workItemTypeId={item.workItemTypeId}
          etapaAtual={item.flowStepId}
          salvando={changeStatus.isPending}
          onFechar={() => setReabrindo(null)}
          onConfirmar={({ comment, flowStepId }) => {
            if (!reabrindo) return
            changeStatus.mutate(
              { statusId: reabrindo.id, comment, flowStepId },
              {
                onSuccess: () => setReabrindo(null),
                onError: (e) =>
                  toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao reabrir.' }),
              },
            )
          }}
        />
        <div className="wi-props__head">
          <h2 className="wi-props__title">Propriedades</h2>
          {/* Configura QUAIS campos a barra mostra (2026-08-30). Fica aqui, e
              não no menu "..." do item, porque é sobre a barra: a lista dela é
              longa e ia afogar o menu. */}
          <div className="wi-menuwrap">
            <button
              type="button"
              className="wi-props__config"
              aria-label="Escolher os campos da barra"
              title="Escolher os campos da barra"
              onClick={() => setConfigAberta((v) => !v)}
            >
              <i className="fas fa-sliders-h" aria-hidden />
            </button>
            <Dropdown open={configAberta} onClose={() => setConfigAberta(false)} width={250}>
              <div className="wi-props__configtit">Mostrar na barra</div>
              {PROPS.map((prop) => {
                const visivel = campoVisivel(prop)
                return (
                  <button
                    key={prop.key}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={visivel}
                    className="wi-menu__opt"
                    onClick={() => alternarCampo(prop)}
                  >
                    {prop.label}
                    <span style={{ flex: 1 }} />
                    <i
                      className="fas fa-check"
                      aria-hidden
                      style={{ fontSize: 11, color: visivel ? 'var(--ob-primary)' : 'transparent' }}
                    />
                  </button>
                )
              })}
            </Dropdown>
          </div>
        </div>
        {/* tópicos "formulário": botão no topo da barra que abre um modal */}
        <CamposComplementaresForm
          workItemId={item.id}
          workItemTypeId={item.workItemTypeId}
          customValues={(item as { customValues?: Record<string, unknown> }).customValues ?? {}}
          item={item}
          fieldHelp={mapaFieldHelp}
        />
        <div className="wi-props__list">
          {PROPS.map((prop) => {
            const { text, avatar, dot, icon } =
              prop.key === 'portfolio' ? { text: portfolioName, avatar: undefined, dot: undefined, icon: undefined } : valueOf(prop)
            const isOpen = openMenu === prop.key
            /**
             * Datas ficam SEMPRE visíveis, mesmo vazias (pedido 2026-08-30):
             * é o primeiro campo da barra e serve para preencher ali mesmo. Só
             * some quando o usuário desliga "Datas" no menu "..." do item.
             */
            // campo desligado na configuração da barra (2026-08-30)
            if (!campoVisivel(prop)) return null
            return (
              <Fragment key={prop.key}>
              {/* campos complementares da BARRA vêm antes de "Criado em" */}
              {prop.key === 'createdAt' ? (
                <CamposComplementaresProps
                  workItemId={item.id}
                  workItemTypeId={item.workItemTypeId}
                  customValues={(item as { customValues?: Record<string, unknown> }).customValues ?? {}}
                  fieldHelp={mapaFieldHelp}
                  item={item}
                />
              ) : null}
              <div>
                <span className="wi-prop__label">
                  {prop.label}
                  {/* "i" de instrucoes (2026-09-01): padrao do catalogo,
                      personalizavel por item pelo lapis da modal */}
                  <CampoInfo
                    chave={`prop:${prop.key}`}
                    titulo={prop.label}
                    ajudaPadrao={ajudaDe(prop.key)}
                    workItemId={item.id}
                    fieldHelp={mapaFieldHelp}
                  />
                </span>
                {/* QUANTIDADE DE DIAS: o campo sozinho, com + e - (2026-08-31).
                    Grava direto, sem menu; com as duas datas postas a mao ele
                    fica travado mostrando o intervalo entre elas. */}
                {prop.key === 'dias' ? (
                  <NumberStepper
                    label="Quantidade de dias"
                    suffix="dia(s)"
                    disabled={Boolean(item.startAt && item.dueAt)}
                    value={
                      item.startAt && item.dueAt
                        ? diasEntre(new Date(item.startAt).toISOString(), new Date(item.dueAt).toISOString())
                        : (item.durationDays ?? null)
                    }
                    onChange={(v) => salvarDias.mutate(v)}
                  />
                ) : null}
                {/* MEMBROS: avatares sobrepostos + "+" que abre o painel */}
                {prop.key === 'membros' ? (
                  <div style={{ position: 'relative' }}>
                    <div className="wi-prop__membros">
                      {membrosDoItem.map((m) => (
                        <span key={m.id} className="wi-chip-member" style={{ width: 26, height: 26, fontSize: 10 }} title={m.name}>
                          {initialsOf(m.name)}
                        </span>
                      ))}
                      <button
                        type="button"
                        className="wi-chip-add"
                        style={{ width: 26, height: 26 }}
                        aria-label="Adicionar ou remover membros"
                        title="Adicionar ou remover membros"
                        onClick={() => setOpenMenu(isOpen ? null : prop.key)}
                      >
                        <i className="fas fa-plus" aria-hidden style={{ fontSize: 10 }} />
                      </button>
                    </div>
                    {isOpen ? <MembersPanel detail={detail} members={members} onClose={() => setOpenMenu(null)} /> : null}
                  </div>
                ) : prop.key === 'dias' ? null : (
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="wi-prop__btn"
                    style={prop.readonly ? { cursor: 'default' } : undefined}
                    onClick={() => {
                      if (prop.readonly) return
                      setOpenMenu(isOpen ? null : prop.key)
                      setQuery('')
                    }}
                  >
                    {avatar ? (
                      <span className="wi-chip-member" style={{ width: 22, height: 22, fontSize: 9 }}>
                        {avatar}
                      </span>
                    ) : null}
                    {dot ? (
                      <span aria-hidden style={{ width: 9, height: 9, borderRadius: 9999, background: dot, flex: 'none' }} />
                    ) : null}
                    {icon ? (
                      <i className={icon.cls} aria-hidden style={{ fontSize: 12, width: 15, flex: 'none', color: icon.color ?? 'var(--ob-text-subtle)' }} />
                    ) : null}
                    <span className={`wi-prop__value${text ? '' : ' wi-prop__value--empty'}`}>{text ?? '—'}</span>
                    {!prop.readonly ? <i className="fas fa-chevron-down wi-prop__chev" aria-hidden /> : null}
                  </button>

                  {/* datas usam o painel de datas do item, ancorado no campo */}
                  {isOpen && prop.key === 'datas' ? (
                    <DatesPanel detail={detail} onClose={() => setOpenMenu(null)} />
                  ) : null}

                  {isOpen && prop.key !== 'datas' ? (
                    <>
                      <div
                        className="wi-menu__backdrop"
                        onClick={() => {
                          setOpenMenu(null)
                          setTrocandoFluxo(false)
                        }}
                      />
                      <div className="wi-prop__menu desc-scroll">
                        {prop.searchable ? (
                          <input
                            className="wi-panel__search"
                            style={{ height: 30, fontSize: 12, marginBottom: 5 }}
                            autoFocus
                            placeholder="Buscar membro…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                          />
                        ) : null}

                        {prop.key === 'status' ? (
                          <>
                            {bootstrap.workItemStatuses
                              .filter((row) => row.isActive)
                              .map((row) => (
                                <button
                                  key={row.id}
                                  type="button"
                                  className={`wi-prop__opt${row.id === item.workItemStatusId ? ' wi-prop__opt--on' : ''}`}
                                  onClick={() => selectValue(prop, row.id)}
                                >
                                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: TONE_COLOR[row.tone], flex: 'none' }} aria-hidden />
                                  {catalogLabel(row)}
                                </button>
                              ))}
                          </>
                        ) : prop.key === 'priority' ? (
                          <>
                            {bootstrap.priorities
                              .filter((row) => row.isActive)
                              .map((row) => (
                                <button
                                  key={row.id}
                                  type="button"
                                  className={`wi-prop__opt${row.id === item.workItemPriorityId ? ' wi-prop__opt--on' : ''}`}
                                  onClick={() => selectValue(prop, row.id)}
                                >
                                  <i className="fas fa-flag" aria-hidden style={{ fontSize: 10, width: 15, color: TONE_COLOR[row.tone] }} />
                                  {catalogLabel(row)}
                                </button>
                              ))}
                          </>
                        ) : prop.tree ? (
                          <>
                            {visibleTree.map((node) => {
                              const hasChildren = treeNodes.some((n) => n.parentId === node.id)
                              const nodeExpanded = expandedNodes.has(node.id)
                              return (
                                <div key={node.id} style={{ display: 'flex', alignItems: 'center' }}>
                                  <button
                                    type="button"
                                    aria-label="Expandir/recolher"
                                    className="wi-cal__navbtn"
                                    style={{ width: 18, height: 18, marginLeft: node.depth * 12, visibility: hasChildren ? 'visible' : 'hidden' }}
                                    onClick={() => toggleNode(node.id)}
                                  >
                                    <i className={`fas fa-caret-${nodeExpanded ? 'down' : 'right'}`} aria-hidden style={{ fontSize: 8 }} />
                                  </button>
                                  <button
                                    type="button"
                                    className={`wi-prop__opt${item.portfolioNodeId === node.id ? ' wi-prop__opt--on' : ''}`}
                                    onClick={() => selectValue(prop, node.id)}
                                  >
                                    <i
                                      className={
                                        {
                                          SOLUTION: 'fas fa-cube',
                                          PRODUCT: 'fas fa-box',
                                          MODULE: 'fas fa-th-large',
                                          FEATURE: 'fas fa-puzzle-piece',
                                          ACTION: 'fas fa-bolt',
                                        }[node.level] ?? 'fas fa-circle'
                                      }
                                      aria-hidden
                                      style={{ fontSize: 10, color: 'var(--ob-text-subtle)', width: 14, flex: 'none' }}
                                    />
                                    {node.name}
                                  </button>
                                </div>
                              )
                            })}
                          </>
                        ) : prop.key === 'assignee' ? (
                          <>
                            <button type="button" className="wi-prop__opt" onClick={() => selectValue(prop, null)}>
                              — Sem responsável
                            </button>
                            {members
                              .filter((person) => person.name.toLowerCase().includes(query.toLowerCase()))
                              .map((person) => (
                                <button
                                  key={person.id}
                                  type="button"
                                  className={`wi-prop__opt${item.assigneeMemberId === person.id ? ' wi-prop__opt--on' : ''}`}
                                  onClick={() => selectValue(prop, person.id)}
                                >
                                  <span className="wi-chip-member" style={{ width: 22, height: 22, fontSize: 9 }}>
                                    {initialsOf(person.name)}
                                  </span>
                                  {person.name}
                                </button>
                              ))}
                          </>
                        ) : prop.key === 'etapa' ? (
                          <>
                            {/* INCLUSÃO e TEMPLATE mostram só as etapas
                                INICIAIS dos fluxos do tipo: na inclusão o item
                                ainda está nascendo (2026-08-29); no template um
                                modelo nunca começa no meio do caminho
                                (2026-08-30). Nos dois casos a lista não avança
                                para as próximas etapas. */}
                            {modoInclusao || modoTemplate ? (
                              etapasIniciaisDisponiveis.length > 0 ? (
                                etapasIniciaisDisponiveis.map((no) => (
                                  <button
                                    key={no.flowStepId}
                                    type="button"
                                    className={`wi-prop__opt${item.flowStepId === no.flowStepId ? ' wi-prop__opt--on' : ''}`}
                                    title={`${no.flowName} · ${no.stepName}`}
                                    onClick={() => selectValue(prop, no.flowStepId)}
                                  >
                                    <span
                                      style={{ width: 9, height: 9, borderRadius: '50%', background: no.stepColor ?? 'var(--ob-primary)', flex: 'none' }}
                                      aria-hidden
                                    />
                                    {no.stepName}
                                    <span style={{ flex: 1 }} />
                                    <span style={{ fontSize: 11, color: 'var(--ob-text-muted)' }}>{no.flowName}</span>
                                  </button>
                                ))
                              ) : (
                                <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 12, color: 'var(--ob-text-muted)', textAlign: 'center', padding: '8px 0', margin: 0 }}>
                                  Nenhum fluxo disponível para este tipo.
                                </p>
                              )
                            ) : item.flowStepId !== null && !trocandoFluxo ? (
                              <>
                                {/* COM etapa: as PROXIMAS do caminho. Tipo SEM
                                    fluxo e flexível — pode sair do fluxo ou
                                    trocar para qualquer outro (2026-08-29). */}
                                {/* sair do fluxo vale para qualquer tipo (SPEC-20261007-2140-item-sem-fluxo) */}
                                <button type="button" className="wi-prop__opt" onClick={() => selectValue(prop, null)}>
                                  — Sem etapa
                                </button>
                                {tipoTemFluxo ? null : (
                                  <>
                                    <button
                                      type="button"
                                      className="wi-prop__opt"
                                      onClick={() => {
                                        setTrocandoFluxo(true)
                                        setFluxoEscolhido(null)
                                      }}
                                    >
                                      <i className="fas fa-route" aria-hidden style={{ fontSize: 10, width: 14, color: 'var(--ob-primary)' }} />
                                      Trocar de fluxo…
                                    </button>
                                  </>
                                )}
                                {proximasDoItem.map((no) => (
                                  <button
                                    key={no.flowStepId}
                                    type="button"
                                    className="wi-prop__opt"
                                    title={`${no.flowName} · ${no.stepName}`}
                                    onClick={() => selectValue(prop, no.flowStepId)}
                                  >
                                    <span
                                      style={{ width: 9, height: 9, borderRadius: '50%', background: no.stepColor ?? 'var(--ob-primary)', flex: 'none' }}
                                      aria-hidden
                                    />
                                    {no.stepName}
                                  </button>
                                ))}
                                {proximasDoItem.length === 0 && flowNodes.data ? (
                                  <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 12, color: 'var(--ob-text-muted)', textAlign: 'center', padding: '8px 0', margin: 0 }}>
                                    Esta etapa não tem próxima etapa no fluxo.
                                  </p>
                                ) : null}
                              </>
                            ) : fluxoEscolhido ? (
                              /* SEM etapa: escolhe o fluxo (acima), depois lista
                                 as etapas dele na ordem do desenho */
                              <>
                                <button
                                  type="button"
                                  className="wi-prop__opt"
                                  onClick={() => setFluxoEscolhido(null)}
                                >
                                  <i className="fas fa-arrow-left" aria-hidden style={{ fontSize: 10, width: 15, color: 'var(--ob-text-subtle)' }} />
                                  Trocar fluxo
                                </button>
                                {etapasEmOrdem(fluxoEscolhido).map((no) => (
                                  <button
                                    key={no.flowStepId}
                                    type="button"
                                    className="wi-prop__opt"
                                    onClick={() => selectValue(prop, no.flowStepId)}
                                  >
                                    <span
                                      style={{ width: 9, height: 9, borderRadius: '50%', background: no.stepColor ?? 'var(--ob-primary)', flex: 'none' }}
                                      aria-hidden
                                    />
                                    {no.stepName}
                                  </button>
                                ))}
                              </>
                            ) : fluxosUnicos.length > 0 ? (
                              /* SEM etapa e sem fluxo escolhido: lista os fluxos */
                              fluxosUnicos.map(([flowId, flowName]) => (
                                <button
                                  key={flowId}
                                  type="button"
                                  className="wi-prop__opt"
                                  onClick={() => setFluxoEscolhido(flowId)}
                                >
                                  <i className="fas fa-route" aria-hidden style={{ fontSize: 10, width: 15, color: 'var(--ob-primary)' }} />
                                  {flowName}
                                </button>
                              ))
                            ) : (
                              <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 12, color: 'var(--ob-text-muted)', textAlign: 'center', padding: '8px 0', margin: 0 }}>
                                Nenhum fluxo disponível neste workspace.
                              </p>
                            )}
                            {flowNodes.isError ? (
                              <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 12, color: 'var(--ob-text-muted)', textAlign: 'center', padding: '8px 0', margin: 0 }}>
                                Fluxos indisponíveis neste plano.
                              </p>
                            ) : null}
                          </>
                        ) : prop.key === 'quadros' ? (
                          /*
                            Vários de uma vez: o menu fica aberto e cada clique
                            liga ou desliga um quadro. "Todos" limpa a escolha.
                          */
                          <>
                            <button
                              type="button"
                              className={`wi-prop__opt${idsDosQuadros.size === 0 ? ' wi-prop__opt--on' : ''}`}
                              onClick={() => salvarQuadros.mutate([])}
                            >
                              <i className="fas fa-globe" aria-hidden style={{ width: 16, color: 'var(--ob-text-subtle)' }} />
                              Todos os quadros
                            </button>
                            {todosOsQuadros.isPending ? (
                              <p className="wi-prop__vazio">Carregando quadros…</p>
                            ) : (
                              (todosOsQuadros.data?.boards ?? []).map((quadro) => {
                                const marcado = idsDosQuadros.has(quadro.id)
                                return (
                                  <button
                                    key={quadro.id}
                                    type="button"
                                    role="menuitemcheckbox"
                                    aria-checked={marcado}
                                    className={`wi-prop__opt${marcado ? ' wi-prop__opt--on' : ''}`}
                                    disabled={salvarQuadros.isPending}
                                    onClick={() => alternarQuadro(quadro.id)}
                                  >
                                    <i
                                      className={marcado ? 'fas fa-check-square' : 'far fa-square'}
                                      aria-hidden
                                      style={{ width: 16, color: marcado ? 'var(--ob-primary)' : 'var(--ob-text-subtle)' }}
                                    />
                                    {t(quadro.nameTranslationKey)}
                                  </button>
                                )
                              })
                            )}
                            <p className="wi-prop__vazio">
                              Sem nenhum marcado, o item aparece em todos os quadros que o alcançam.
                            </p>
                          </>
                        ) : prop.key === 'area' ? (
                          bootstrap.areas.map((area) => (
                            <button
                              key={area.id}
                              type="button"
                              className={`wi-prop__opt${item.companyAreaId === area.id ? ' wi-prop__opt--on' : ''}`}
                              onClick={() => selectValue(prop, area.id)}
                            >
                              {areaName(area.id)}
                            </button>
                          ))
                        ) : (
                          <>
                            <button type="button" className="wi-prop__opt" onClick={() => selectValue(prop, null)}>
                              — Sem empresa
                            </button>
                            {bootstrap.companies.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                className={`wi-prop__opt${item.companyId === client.id ? ' wi-prop__opt--on' : ''}`}
                                onClick={() => selectValue(prop, client.id)}
                              >
                                {client.name}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
                )}
              </div>
              </Fragment>
            )
          })}
        </div>
      </aside>
    </>
  )
}
